import React, { useMemo, useState } from 'react';
import {
	Card,
	Stack,
	Typography,
	FormLabel,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Skeleton,
	useTheme,
} from '@mui/material';
import { DEAL_STATUS_TYPES, formatDate } from '@/utils/constants';
import { CollapsibleTaskGroup } from '@/components/CollapsibleTaskGroup';
import { DealCardAdviserFee } from '@/components/cards/DealCardValues/DealCardAdviserFee';
import { DealCardDateSelect } from '@/components/cards/DealCardValues/DealCardDateSelect';
import { DealCardStageChange } from '@/components/cards/DealCardValues/DealCardStageChange';
import { ClaimNumberField } from '@/components/ClaimNumberField';
import { AssociatedClient } from '@/components/cards/DealCardValues/AssociatedClient';
import { useObjectContext } from '@/hooks/useObjectContext';
import { NavigateInsightButton } from '@/components/buttons/NavigateInsightButton';
import { ClaimTypeSelectWrapper } from '@/components/selects/ClaimTypeSelect';
import { ExpandMore } from '@mui/icons-material';
import { ComplianceTasksDisplay } from '@/components/ComplianceTasksDisplay';
import { ChangeAssigneeField } from '@/components/cards/DealCardValues/ChangeAssigneeField';
import { useParams } from 'react-router-dom';
import { useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import { CompleteDealButton } from '@/components/buttons/CompleteDealButton';
import { usePipelineContext } from '@/context/pipelineContext';
import { DealMenu } from '@/components/DealMenu';
import { YearFolderPicker } from '@/components/cards/DealCardValues/YearFolderSelectorChange';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { DealAmount } from '@/components/cards/DealCardValues/DealAmount';
import AskAiButton from '@/components/buttons/viewHeaderButtons/AskAiButton.jsx';
import { InformedConsentCard } from '@/components/views/ClientView/InformedConsentCard';

const DealAboutAccordion = ({ title, children }) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState(true);

	return (
		<Accordion
			elevation={0}
			disableGutters
			square
			expanded={expanded}
			onChange={() => setExpanded((prev) => !prev)}
			sx={{
				backgroundColor: 'transparent',
				width: '100%',
				'&:not(:last-child)': {
					borderBottom: 0,
				},
				'&::before': {
					display: 'none',
				},
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
				sx={{
					borderBottom: expanded ? `1px solid ${theme.palette.divider}` : 'none',
					padding: 0,
					marginBottom: '1em',
				}}
			>
				<Typography variant='subtitle1'>{title}</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ padding: '0px' }}>{children}</AccordionDetails>
		</Accordion>
	);
};
DealAboutAccordion.displayName = 'DealAboutAccordion';

export const DealAboutCard = () => {
	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);

	const { dealId } = useParams();
	const { dealRow, clientRow, clientId, broker } = useObjectContext();

	const pipelineId = dealRow?.pipeline;
	const { stages, ...pipelineQuery } = usePipelineContext(pipelineId);

	const { data: policyData } = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });
	const policies = policyData?.policies ?? [];

	const isDeal = dealRow?.objectType === 'deal';

	const dealStage = useMemo(() => {
		const stageId = dealRow?.dealStage;
		const stage = stages?.find((stage) => stage.id === stageId);
		return stage;
	}, [stages, dealRow?.dealStage]);

	const isDisabled = Boolean(dealStage?.completed);

	const isDealArchived = useMemo(
		() => dealRow?.status === DEAL_STATUS_TYPES.archived,
		[dealRow?.status],
	);

	const isEndorsement = !isDeal && pipelineId == process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isNewBusiness = pipelineId == process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isClaim = pipelineId == process.env.REACT_APP_PIPELINE_CLAIMS;

	const showInformedConsent = Boolean(getFeatureFlags('informedConsent')?.active) && !isClaim;

	const dealCardValues = [
		<ChangeAssigneeField key={'deal-card-value-1'} />,
		isNewBusiness && showNewBusinessKPI ? (
			<Stack direction='column' spacing={3}>
				<DealAmount key='deal-card-value-2' />
				<DealCardDateSelect
					key={'deal-card-value-5'}
					title={'Estimated close date'}
					dealKey={'estimatedCloseDate'}
				/>
			</Stack>
		) : (
			<DealCardAdviserFee key='deal-card-value-2' />
		),

		<DealCardDateSelect
			key={'deal-card-value-4'}
			title={'Renewal date'}
			dealKey={'renewalDate'}
		/>,
	];

	const ticketCardValues = [
		<ChangeAssigneeField key={'ticket-card-value-1'} />,
		...(!isEndorsement
			? [
					<DealCardDateSelect
						key={'ticket-card-value-3'}
						title={'Date of Loss'}
						dealKey={'LossDate'}
						disableFuture={true}
					/>,
					<>
						<FormLabel>Claim Number</FormLabel>
						<ClaimNumberField />
					</>,
					<>
						<FormLabel>Insurer</FormLabel>
						<Typography>
							{dealRow?.insurer ?? policies?.[0]?.InsurerName ?? 'Unknown'}
						</Typography>
					</>,
					<ClaimTypeSelectWrapper key={'ticket-card-value-claim-type'} minimal={true} />,
				]
			: []),
		<>
			{!isClaim && (
				<>
					<FormLabel>{isEndorsement === true ? 'Created date ' : 'Open date'}</FormLabel>
					<Typography>{formatDate(dealRow?.createDate, true)}</Typography>
				</>
			)}
		</>,
	];

	return (
		<Card
			variant='about-card'
			data-testid='about-card'
			sx={{ px: 3, py: 2, borderTopRightRadius: 0, borderTopLeftRadius: 0 }}
		>
			<Stack direction='column' spacing={2}>
				<Stack spacing={1}>
					<Typography variant='subtitle1'>Actions</Typography>
					{broker?.ledger && clientRow?.insightId && (
						<NavigateInsightButton insightId={clientRow?.insightId} />
					)}
					<DealMenu />
					<AskAiButton />

					{!isDisabled && !isDealArchived && (
						<>
							{!pipelineQuery.isSuccess ? (
								<Skeleton height='3em' />
							) : (
								<CompleteDealButton
									pipelineId={pipelineId}
									isDisabled={isDisabled}
								/>
							)}
						</>
					)}
				</Stack>

				<DealAboutAccordion title='Compliance'>
					<Stack direction='column' spacing={3}>
						{showInformedConsent && <InformedConsentCard clientId={clientId} />}
						<YearFolderPicker minimal={true} />
						<DealCardStageChange
							minimal={true}
							{...(!isEndorsement && {
								title: isDeal ? 'Deal Stage' : 'Claim Status',
							})}
							{...(isClaim && { showReopen: true })}
						/>
						<ComplianceTasksDisplay />
					</Stack>
				</DealAboutAccordion>
				<DealAboutAccordion title='About'>
					<AssociatedClient />
					<Stack direction='column' marginTop={3} spacing={3}>
						{(isDeal ? dealCardValues : ticketCardValues).map((e, i) => (
							<Stack spacing={1} key={`about-values-${i}`}>
								{e}
							</Stack>
						))}
					</Stack>
				</DealAboutAccordion>
				<CollapsibleTaskGroup />
			</Stack>
		</Card>
	);
};
DealAboutCard.displayName = 'DealAboutCard';
